import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Option as RadioOption } from '@node-space/storybook-components/dist/components/Radio/Radio'
import { useFormFields } from 'hooks/forms/useFormFields'
import { DynamicQuestionAnswer, DynamicQuestionPayload } from 'types/dynamicFields'
import { ConfirmValues, DynamicQuestionFieldTypes } from './constants'
import { DynamicQuestionMapping, DynamicQuestionsStep, StepUnsavedChanges } from './types'
import { useDynamicQuestionSchema } from './useDynamicQuestionSchema'
import {
  getAdditionalFieldKey,
  getContactEmailFieldKey,
  getContactLastNameFieldKey,
  getFieldDefaultValue,
} from './utils'

export const useDynamicQuestionsForm = (
  questions: DynamicQuestionsStep,
  unsavedChanges: StepUnsavedChanges
) => {
  const { t } = useTranslation()
  const {
    getFieldSchema,
    getAdditionalFieldSchema,
    getContactLastNameFieldSchema,
    getContactEmailFieldSchema,
  } = useDynamicQuestionSchema()

  const yesNoOptions: RadioOption[] = [
    { value: ConfirmValues.YES, secondaryLabel: t('yes') },
    { value: ConfirmValues.NO, secondaryLabel: t('no') },
  ]

  const questionKeys = Object.keys(questions) ?? []

  const defaultValues =
    unsavedChanges ??
    questionKeys?.reduce((acc, key) => {
      const defaultValue = getFieldDefaultValue(key, questions?.[key])
      return { ...acc, ...defaultValue }
    }, {})

  const schema = questionKeys?.reduce((acc, key) => {
    const question = questions?.[key]
    acc[key] = getFieldSchema(key, question?.type)
    if (question?.additionalQuesion) {
      acc[getAdditionalFieldKey(key)] = getAdditionalFieldSchema(key)
    }
    if (question?.type === DynamicQuestionFieldTypes.CONTACT) {
      acc[getContactLastNameFieldKey(key)] = getContactLastNameFieldSchema()
      acc[getContactEmailFieldKey(key)] = getContactEmailFieldSchema()
    }
    return acc
  }, {})

  const formSchema = yup.object<Partial<Record<string, yup.AnySchema>>>(schema)
  const form = useForm<Record<string, string>>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const { control, clearErrors } = form
  const formValues = useWatch({ control })

  const { setEmailField, setInputField, setNumberInputField, setRadioField, setTextAreaField } =
    useFormFields<string>(formSchema, form, formValues)

  const getFormField = (key: string) => {
    const question = questions?.[key]
    switch (question?.type) {
      case DynamicQuestionFieldTypes.RADIO:
        return setRadioField({
          key,
          options: yesNoOptions,
          ...(question?.additionalQuesion && {
            onChangeAdditional: value => {
              if (value !== ConfirmValues.YES) {
                clearErrors(getAdditionalFieldKey(key))
              }
            },
          }),
        })
      case DynamicQuestionFieldTypes.NUMBER:
        return setNumberInputField({ key, placeholder: '0' })
      case DynamicQuestionFieldTypes.EMAIL:
        return setEmailField({ key })
      case DynamicQuestionFieldTypes.CONTACT:
        return setInputField({ key })
      default:
        return setTextAreaField({ key })
    }
  }

  const formFields = questionKeys?.reduce((acc, key) => {
    const question = questions?.[key]
    acc[key] = getFormField(key)
    if (question?.additionalQuesion) {
      const additionalKey = getAdditionalFieldKey(key)
      acc[additionalKey] = setTextAreaField({ key: additionalKey })
    }
    if (question?.type === DynamicQuestionFieldTypes.CONTACT) {
      const lastNameKey = getContactLastNameFieldKey(key)
      const emailKey = getContactEmailFieldKey(key)
      acc[lastNameKey] = setInputField({ key: lastNameKey })
      acc[emailKey] = setEmailField({ key: emailKey })
    }
    return acc
  }, {})

  const getQuestionAnswer = (
    code: string,
    question: DynamicQuestionMapping
  ): DynamicQuestionAnswer => {
    switch (question?.type) {
      case DynamicQuestionFieldTypes.CONTACT:
        return {
          contact: {
            first_name: formValues?.[code],
            last_name: formValues?.[getContactLastNameFieldKey(code)],
            email: formValues?.[getContactEmailFieldKey(code)],
          },
        }
      case DynamicQuestionFieldTypes.RADIO:
        const selected = formValues?.[code]
        const isYesSelected = selected === ConfirmValues.YES
        return {
          selected,
          ...(question?.additionalQuesion && {
            answer: isYesSelected ? formValues?.[getAdditionalFieldKey(code)] : '',
          }),
        }
      default:
        return {
          answer: formValues?.[code],
        }
    }
  }

  const getUpdatedQuestions = () => {
    const mapping: DynamicQuestionsStep = { ...questions }
    const payload: DynamicQuestionPayload[] = Object.entries(questions)?.map(([code, question]) => {
      const answer = getQuestionAnswer(code, question)
      mapping[code].savedAnswer = answer
      return { code, value: { answer: JSON.stringify(answer) } }
    })
    return { mapping, payload }
  }

  return { formFields, form, defaultValues, formValues, getUpdatedQuestions }
}
